<script setup lang="ts">
// Multi-series chart combining bars and lines on a shared x-axis.
// Each series declares its `kind: "bar" | "line"` and an optional
// `yAxis: 0 | 1 | 2 | 3`. When any series uses `yAxis > 0`, the
// `yAxes[]` array is required and each used index must have a
// `label`; the validator enforces.

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { MixedChartSpec } from "../sbi/protocol";
import { LINE_STROKE_WIDTH, PALETTE } from "../theme.ts";
import {
  axisLabelDefaults,
  axisLineDefaults,
  baseOption,
  splitLineDefaults,
} from "./echarts-base";
import {
  axisTickFormatter,
  dataZoomOption,
  estimateLegendRows,
  LEGEND_ROW_TEXT,
  NARROW,
  plotWidthEstimate,
} from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

/** Room for a rotated axis name outside its tick labels, px. */
const AXIS_NAME_ROOM = 22;

const props = withDefaults(
  defineProps<{
    spec: MixedChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const xValues = computed(() => {
  const rows = props.spec.data as ReadonlyArray<Record<string, unknown>>;
  return rows.map((r) => r[props.spec.x]);
});

const seriesUsedAxes = computed(() => {
  const set = new Set<number>();
  for (const s of props.spec.series) set.add(s.yAxis ?? 0);
  return [...set].sort((a, b) => a - b);
});

const isNarrow = computed(() => ctx.value.width > 0 && ctx.value.width < NARROW);

const yAxes = computed(() => {
  const used = seriesUsedAxes.value;
  const declared = props.spec.yAxes ?? [];
  const narrow = isNarrow.value;
  const plotWidth = plotWidthEstimate(ctx.value.width);
  return used.map((idx) => {
    const decl = declared[idx];
    const isLeft = idx === 0;
    // Narrow: a third / fourth axis can't fit beside the plot. Keep it
    // (its series still scale against it) but hide its ticks and rule;
    // the tooltip still reads the values.
    const hidden = narrow && idx >= 2;
    return {
      type: "value" as const,
      name: hidden ? undefined : decl?.label,
      // Wide: rotated name centred beside the axis. Narrow: the name
      // moves above the axis end so it doesn't cost plot width.
      ...(narrow
        ? {
            nameLocation: "end" as const,
            nameGap: 6,
            nameTextStyle: {
              ...axisLabelDefaults(),
              align: isLeft ? ("left" as const) : ("right" as const),
            },
          }
        : {
            nameLocation: "middle" as const,
            nameGap: 40,
            // Rotated axis name picks up the same mono register as the
            // ticks; without this it falls back to ECharts' default sans
            // and reads as a different family from its own tick labels.
            nameTextStyle: { ...axisLabelDefaults() },
          }),
      position: isLeft ? "left" : ("right" as const),
      // Stack right-side axes outward. Index 0 (left) has no offset;
      // index 1 sits flush at the right edge; indices 2, 3 step further
      // right — 60px apart, 44px on narrow charts.
      offset: idx > 1 ? (idx - 1) * (narrow ? 44 : 60) : 0,
      // Only the left (primary) axis paints dashed gridlines — drawing
      // splitLines for every axis would multi-layer the grid and read
      // as visual noise. Right axes show a thin baseline rule so the
      // viewer can see where the axis is.
      axisLine: isLeft || hidden ? { show: false } : { ...axisLineDefaults() },
      splitLine: isLeft ? { ...splitLineDefaults() } : { show: false },
      axisLabel: {
        ...axisLabelDefaults(),
        show: !hidden,
        // The declared per-axis format (documented on `MixedYAxis`),
        // compacted on narrow plots.
        formatter: axisTickFormatter(decl?.format, plotWidth),
      },
    };
  });
});

/**
 * Margins for the axes. `containLabel` (base grid) already fits every
 * axis's tick labels, offsets included, but not axis *names*: a wide
 * chart's rotated names sit outside the labels and need their own room
 * on each side; a narrow chart's names sit above the plot instead.
 */
const axisMargins = computed(() => {
  const declared = props.spec.yAxes ?? [];
  const used = seriesUsedAxes.value;
  const named = (idx: number) => !!declared[idx]?.label;
  if (isNarrow.value) {
    return { left: 8, right: 16, top: used.some(named) ? 16 : 0 };
  }
  const rightNamed = used.filter((i) => i !== 0 && named(i)).length;
  return {
    left: 8 + (used.includes(0) && named(0) ? AXIS_NAME_ROOM : 0),
    right: 16 + rightNamed * AXIS_NAME_ROOM,
    top: 0,
  };
});

const option = computed(() => {
  const base = baseOption({ title: props.spec.title }, ctx.value);
  const series = props.spec.series.map((s, i) => ({
    type: s.kind,
    name: s.name,
    yAxisIndex: seriesUsedAxes.value.indexOf(s.yAxis ?? 0),
    data: (props.spec.data as ReadonlyArray<Record<string, unknown>>).map(
      (row) => row[s.y],
    ),
    itemStyle: { color: PALETTE[i % PALETTE.length] },
    // Line-kind series get the same polished treatment as the line
    // chart proper — gentle smoothing, confident stroke, no per-point
    // dots. Bars ignore these fields silently (ECharts checks
    // `type === "line"` before honouring them).
    ...(s.kind === "line"
      ? {
          smooth: 0.4,
          showSymbol: false,
          symbol: "circle",
          lineStyle: { width: LINE_STROKE_WIDTH, color: PALETTE[i % PALETTE.length] },
        }
      : {}),
  }));
  // Bottom-anchored eyebrow legend, stacked above the zoom slider when
  // there is one (the slider used to paint over the legend). The grid
  // reserves the legend's real rows — print wraps it.
  const zoom = dataZoomOption(props.spec.dataZoom, ctx.value);
  const names = props.spec.series.map((s) => s.name ?? "");
  const itemGap = isNarrow.value ? 12 : 18;
  const legendRows =
    ctx.value.print && ctx.value.width > 0
      ? estimateLegendRows(names, ctx.value.width - 16, itemGap)
      : 1;
  const legendHeight = legendRows * (LEGEND_ROW_TEXT + itemGap) - itemGap;
  const margins = axisMargins.value;
  return {
    ...base,
    grid: {
      ...base.grid,
      left: margins.left,
      right: margins.right,
      top: base.grid.top + margins.top,
      bottom: 8 + legendHeight + 10 + zoom.reserveBottom,
    },
    // Spread `base.legend` so we keep mono + uppercase + tracked +
    // circle markers; only override position.
    legend: { ...base.legend, itemGap, top: undefined, bottom: zoom.reserveBottom },
    // Spread `base.tooltip` so the mono textStyle + axisPointer travel.
    tooltip: { ...base.tooltip },
    xAxis: {
      ...base.xAxis,
      type: "category" as const,
      data: xValues.value,
      axisLabel: { ...base.xAxis.axisLabel, hideOverlap: true },
    },
    yAxis: yAxes.value,
    series,
    dataZoom: zoom.dataZoom,
  };
});

const height = computed(() =>
  resolveChartHeight("mixed", props.spec.height),
);

function onClick(p: {
  seriesName?: string;
  seriesIndex?: number;
  dataIndex?: number;
  value?: unknown;
}) {
  if (!props.spec.id || typeof p?.dataIndex !== "number") return;
  const series = props.spec.series[p.seriesIndex ?? 0];
  emit("click-target", props.spec.id, {
    seriesName: p.seriesName ?? "",
    seriesType: series?.kind ?? "",
    x: xValues.value[p.dataIndex],
    y: typeof p.value === "number" ? p.value : Number(p.value),
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
